import React from 'react';
import { motion } from 'framer-motion';
import {
  ServerStackIcon,
  AcademicCapIcon,
  KeyIcon,
  CubeTransparentIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';

const IntegrationDiagram: React.FC = () => {
  const sisLogos = ['Sentral', 'Compass', 'TASS', 'Edumate'];
  const lmsLogos = ['Schoolbox', 'Canvas', 'Google Classroom', 'Teams'];
  const ssoLogos = ['Microsoft', 'Google', 'Okta', 'AD FS'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="relative"
    >
      <div className="card-premium p-8 overflow-hidden">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          {/* SIS Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] p-3">
                <ServerStackIcon className="w-full h-full text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Student Information</h3>
                <p className="text-sm text-[var(--color-medium-gray)]">Systems</p>
              </div>
            </div>
            <div className="space-y-2">
              {sisLogos.map((name, index) => (
                <motion.div
                  key={name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-[var(--color-electric-blue)]/20 flex items-center justify-center">
                    <span className="text-xs font-bold text-[var(--color-electric-blue)]">
                      {name.charAt(0)}
                    </span>
                  </div>
                  <span className="text-white text-sm">{name}</span>
                  <ArrowsRightLeftIcon className="w-4 h-4 text-[var(--color-emerald)] ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Center - Lumen8 Hub */}
          <div className="relative flex flex-col items-center justify-center py-8">
            {/* Connection Lines - Left */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full hidden lg:block">
              <svg className="w-full h-24" viewBox="0 0 200 100" preserveAspectRatio="none">
                {/* Left connections */}
                <motion.path
                  d="M 0 20 Q 50 20 100 50"
                  stroke="url(#gradient-blue)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.2 }}
                  viewport={{ once: true }}
                />
                <motion.path
                  d="M 0 40 Q 50 40 100 50"
                  stroke="url(#gradient-blue)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.3 }}
                  viewport={{ once: true }}
                />
                <motion.path
                  d="M 0 60 Q 50 60 100 50"
                  stroke="url(#gradient-blue)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.4 }}
                  viewport={{ once: true }}
                />
                <motion.path
                  d="M 0 80 Q 50 80 100 50"
                  stroke="url(#gradient-blue)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  viewport={{ once: true }}
                />
                {/* Right connections */}
                <motion.path
                  d="M 100 50 Q 150 20 200 20"
                  stroke="url(#gradient-emerald)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  viewport={{ once: true }}
                />
                <motion.path
                  d="M 100 50 Q 150 40 200 40"
                  stroke="url(#gradient-emerald)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.7 }}
                  viewport={{ once: true }}
                />
                <motion.path
                  d="M 100 50 Q 150 60 200 60"
                  stroke="url(#gradient-emerald)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.8 }}
                  viewport={{ once: true }}
                />
                <motion.path
                  d="M 100 50 Q 150 80 200 80"
                  stroke="url(#gradient-emerald)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.9 }}
                  viewport={{ once: true }}
                />
                <defs>
                  <linearGradient id="gradient-blue" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="var(--color-electric-blue)" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="var(--color-electric-blue)" stopOpacity="1" />
                  </linearGradient>
                  <linearGradient id="gradient-emerald" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="var(--color-emerald)" stopOpacity="1" />
                    <stop offset="100%" stopColor="var(--color-emerald)" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, type: 'spring' }}
              viewport={{ once: true }}
              className="relative z-10"
            >
              <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-[var(--color-electric-blue)] via-[var(--color-violet)] to-[var(--color-emerald)] p-1">
                <div className="w-full h-full rounded-xl bg-[var(--color-midnight)] flex flex-col items-center justify-center">
                  <CubeTransparentIcon className="w-12 h-12 text-[var(--color-electric-blue)] mb-2" />
                  <span className="text-white font-black text-lg">Lumen8</span>
                  <span className="text-[var(--color-medium-gray)] text-xs">API Hub</span>
                </div>
              </div>
              {/* Pulsing ring */}
              <div className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-emerald)] animate-pulse opacity-30 blur-xl"></div>
            </motion.div>

            <div className="mt-6 text-center">
              <p className="text-sm text-[var(--color-light-gray)]">
                Bidirectional sync with all major platforms
              </p>
            </div>
          </div>

          {/* LMS & SSO Column */}
          <div className="space-y-8">
            {/* LMS */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--color-violet)] to-[var(--color-electric-blue)] p-2">
                  <AcademicCapIcon className="w-full h-full text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">LMS</h3>
                  <p className="text-xs text-[var(--color-medium-gray)]">Learning Management</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {lmsLogos.map((name, index) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-2 p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-xs"
                  >
                    <div className="w-6 h-6 rounded bg-[var(--color-violet)]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-[var(--color-violet)]">
                        {name.charAt(0)}
                      </span>
                    </div>
                    <span className="text-white truncate">{name}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* SSO */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--color-emerald)] to-[var(--color-bright-cyan)] p-2">
                  <KeyIcon className="w-full h-full text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Identity</h3>
                  <p className="text-xs text-[var(--color-medium-gray)]">Single Sign-On</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {ssoLogos.map((name, index) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-2 p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-xs"
                  >
                    <div className="w-6 h-6 rounded bg-[var(--color-emerald)]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-[var(--color-emerald)]">
                        {name.charAt(0)}
                      </span>
                    </div>
                    <span className="text-white truncate">{name}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* API Features Footer */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xl font-bold text-[var(--color-electric-blue)]">REST</div>
              <div className="text-xs text-[var(--color-medium-gray)]">API Protocol</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xl font-bold text-[var(--color-violet)]">OAuth 2.0</div>
              <div className="text-xs text-[var(--color-medium-gray)]">Authentication</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xl font-bold text-[var(--color-emerald)]">OneRoster</div>
              <div className="text-xs text-[var(--color-medium-gray)]">Rostering</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xl font-bold text-[var(--color-amber)]">LTI 1.3</div>
              <div className="text-xs text-[var(--color-medium-gray)]">Tool Integration</div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default IntegrationDiagram;
